<!--
  Driver — lista.

  Come per i clienti: caricamento a pagine dal server invece dello scarico
  completo (oltre 1000 record il backend tronca senza dirlo), filtri e
  ordinamento eseguiti dal database.

  ZONE — da M3 il driver ha province strutturate (Regione→Provincia dal seed
  ISTAT). La vecchia `areas` (stringa libera) resta visibile solo per i driver
  non ancora migrati, così si vede a colpo d'occhio chi manca.
-->
<template>
  <div class="drv">
    <div class="drv__head">
      <div>
        <h1 class="drv__title">
          Driver
        </h1>
        <p class="drv__sub">
          Anagrafica driver, agenzia di appartenenza e zone operative.
        </p>
      </div>
      <div class="drv__headActions">
        <el-input
          v-model="search"
          class="drv__search"
          placeholder="Cerca nome, mail, telefono…"
          clearable
          @input="onSearchInput"
        />
        <router-link
          to="/driver/add-driver"
          class="routerLink"
        >
          <el-button type="primary">
            Nuovo driver
          </el-button>
        </router-link>
      </div>
    </div>

    <!-- le card filtrano per stato -->
    <div class="drv__kpis">
      <button
        v-for="k in kpis"
        :key="k.key"
        type="button"
        class="drv__kpi"
        :class="{ 'is-active': status === k.key }"
        @click="setStatus(k.key)"
      >
        <span class="drv__kpiNum">{{ k.value === null ? '—' : k.value }}</span>
        <span class="drv__kpiLbl">{{ k.label }}</span>
      </button>
    </div>

    <div class="drv__card">
      <div class="drv__toolbar">
        <div class="drv__field">
          <label>Agenzia</label>
          <el-select
            v-model="agencyId"
            placeholder="Tutte"
            clearable
            filterable
            style="width: 220px"
            @change="reload"
          >
            <el-option
              v-for="a in agencies"
              :key="a.id"
              :label="a.company_name"
              :value="a.id"
            />
          </el-select>
        </div>
        <!-- La provincia sta accanto all'agenzia, non fra le ricerche in
             alto: sono tutti e due filtri sull'elenco, e si usano insieme. -->
        <div class="drv__field">
          <label>Provincia</label>
          <el-select
            v-model="provinceId"
            placeholder="Tutte"
            clearable
            filterable
            style="width: 220px"
            @change="reload"
          >
            <el-option
              v-for="p in province"
              :key="p.id"
              :label="p.sigla ? `${p.name} (${p.sigla})` : p.name"
              :value="p.id"
            />
          </el-select>
        </div>
        <div class="drv__spacer" />
        <el-button
          text
          @click="resetFilters"
        >
          Azzera filtri
        </el-button>
      </div>

      <el-table
        v-loading="loading"
        :data="rows"
        style="width: 100%"
        empty-text="Nessun driver corrisponde ai filtri"
        @row-click="openDetail"
        @sort-change="onSortChange"
      >
        <el-table-column
          label="Driver"
          prop="surname"
          sortable="custom"
          min-width="230"
        >
          <template #default="scope">
            <div class="drv__who">
              <span class="drv__avatar">{{ ini(scope.row) }}</span>
              <div>
                <div class="drv__name">
                  {{ fullName(scope.row) }}
                </div>
                <div class="drv__meta">
                  {{ scope.row.mail }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Telefono"
          min-width="150"
        >
          <template #default="scope">
            <span class="drv__num">{{ scope.row.telephone_number || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Agenzia"
          min-width="170"
        >
          <template #default="scope">
            {{ scope.row.agency?.company_name || '—' }}
          </template>
        </el-table-column>

        <el-table-column
          label="Zone operative"
          min-width="230"
        >
          <template #default="scope">
            <div
              v-if="scope.row.provinces?.length"
              class="drv__zones"
            >
              <span
                v-for="p in scope.row.provinces.slice(0, 3)"
                :key="p.id"
                class="drv__zone"
              >{{ p.sigla || p.name }}</span>
              <span
                v-if="scope.row.provinces.length > 3"
                class="drv__zoneMore"
              >+{{ scope.row.provinces.length - 3 }}</span>
              <span class="drv__region">{{ regionsOf(scope.row) }}</span>
            </div>
            <!-- driver ancora sulla vecchia struttura ad "aree" -->
            <span
              v-else-if="scope.row.areas?.length"
              class="drv__legacy"
            >
              {{ scope.row.areas.map((a) => a.area).join(', ') }} · da migrare
            </span>
            <span
              v-else
              class="drv__dash"
            >—</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Ruolo"
          width="120"
        >
          <template #default="scope">
            <span
              class="drv__role"
              :class="{ 'is-jolly': scope.row.is_jolly }"
            >{{ scope.row.is_jolly ? 'Jolly' : 'Driver' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="130"
        >
          <template #default="scope">
            <StatusPill
              :kind="isOperativo(scope.row) ? 'ok' : 'muted'"
              :label="isOperativo(scope.row) ? 'Attivo' : 'Non attivo'"
            />
          </template>
        </el-table-column>

        <el-table-column
          width="120"
          align="right"
          label="Azioni"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              @click.stop="openDetail(scope.row)"
            >
              Dettagli
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="drv__foot">
        <span class="drv__footInfo">
          {{ total }} driver
          <template v-if="pageCount > 1">· pagina {{ page }} di {{ pageCount }}</template>
        </span>
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="total"
          :current-page="page"
          @current-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import driverAPI from '../../services/driverAPI.js';
import areasAPI from '../../services/areasAPI.js';
import agencyAPI from '../../services/agencyAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';

const PAGE_SIZE = 25;

export default {
  name: 'DriverPage',
  components: { StatusPill },

  setup() {
    const router = useRouter();

    const rows = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const total = ref(0);
    const pageCount = ref(1);
    const search = ref('');
    const agencyId = ref('');
    const status = ref('attivi');
    const sort = ref('');
    const agencies = ref([]);
    const provinceId = ref('');
    const province = ref([]);
    const counts = ref({ attivi: null, inattivi: null, '': null });

    const kpis = computed(() => [
      { key: 'attivi', label: 'Attivi', value: counts.value.attivi },
      { key: 'inattivi', label: 'Non attivi', value: counts.value.inattivi },
      { key: '', label: 'Totale', value: counts.value[''] },
    ]);

    const load = async () => {
      loading.value = true;
      try {
        const res = await driverAPI.getDriversPaged({
          page: page.value,
          limit: PAGE_SIZE,
          search: search.value,
          agencyId: agencyId.value,
          provinceId: provinceId.value,
          status: status.value,
          sort: sort.value,
        });
        rows.value = res.rows;
        total.value = res.total;
        pageCount.value = res.pageCount || 1;
      } catch (e) {
        console.error('Caricamento driver fallito:', e);
        rows.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const loadCounts = async () => {
      const keys = ['attivi', 'inattivi', ''];
      const res = await Promise.allSettled(keys.map((k) => driverAPI.countDrivers(k)));
      res.forEach((r, i) => {
        if (r.status === 'fulfilled') counts.value[keys[i]] = r.value;
      });
    };

    // Le province in cui Simba opera davvero (quelle con sedi di partner):
    // stesso elenco della capacità driver, così i due schermi non mostrano
    // liste diverse.
    const loadProvince = async () => {
      try {
        province.value = await areasAPI.getPartnerProvinces();
      } catch (e) {
        console.warn('Elenco province non disponibile:', e?.message);
      }
    };

    const loadAgencies = async () => {
      try {
        const r = await agencyAPI.getAgencies();
        agencies.value = (r.data?.data ?? r.data ?? []).map((a) => ({
          id: a.id, company_name: a.company_name,
        }));
      } catch (e) {
        console.warn('Elenco agenzie non disponibile:', e?.message);
      }
    };

    const reload = () => { page.value = 1; load(); };

    const setStatus = (key) => {
      if (status.value === key) return;
      status.value = key;
      reload();
    };

    const onPageChange = (p) => { page.value = p; load(); };

    // L'ordinamento lo fa il database: ordinare in memoria riguarderebbe solo
    // le 25 righe già scaricate, non l'intero elenco.
    const onSortChange = ({ prop, order }) => {
      sort.value = prop && order ? `${prop},${order === 'ascending' ? 'ASC' : 'DESC'}` : '';
      reload();
    };

    let timer = null;
    const onSearchInput = () => {
      clearTimeout(timer);
      timer = setTimeout(reload, 350);
    };

    const resetFilters = () => {
      search.value = '';
      agencyId.value = '';
      provinceId.value = '';
      sort.value = '';
      reload();
    };

    const openDetail = (row) => {
      if (row?.id) router.push(`/driver/detail/${row.id}`);
    };

    const fullName = (d) =>
      [d?.name, d?.surname].filter(Boolean).join(' ') || '—';

    const ini = (d) =>
      `${(d?.name || '')[0] || ''}${(d?.surname || '')[0] || ''}`.toUpperCase() || '?';

    // Regioni distinte delle province assegnate: dà il quadro senza elencare
    // tutte le sigle.
    const regionsOf = (d) => {
      const names = [...new Set((d.provinces || [])
        .map((p) => p.region?.name)
        .filter(Boolean))];
      return names.join(', ');
    };

    onMounted(() => { load(); loadCounts(); loadAgencies(); loadProvince(); });

    return {
      rows, loading, page, total, pageCount, search, agencyId, provinceId, status,
      agencies, province, kpis, pageSize: PAGE_SIZE,
      setStatus, onPageChange, onSortChange, onSearchInput, resetFilters,
      reload, openDetail, fullName, ini, regionsOf,
      isOperativo: driverAPI.isOperativo,
    };
  },
};
</script>

<style scoped>
.drv { padding: 4px 0 32px; }

.drv__head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; flex-wrap: wrap; margin-bottom: 18px;
}
.drv__title { margin: 0; font-size: 22px; color: var(--text-primary); }
.drv__sub { margin: 4px 0 0; font-size: 13px; color: var(--text-tertiary); }
.drv__headActions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.drv__search { width: 260px; }

.drv__kpis {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px; margin-bottom: 16px;
}
@media (max-width: 700px) { .drv__kpis { grid-template-columns: 1fr; } }
.drv__kpi {
  text-align: left; background: var(--bg-primary);
  border: 1px solid var(--border-color); border-radius: var(--radius-lg);
  padding: 13px 15px; cursor: pointer; font-family: inherit;
  transition: border-color var(--transition-fast);
}
.drv__kpi:hover { border-color: var(--brand-violet); }
.drv__kpi.is-active {
  border-color: var(--brand-violet);
  box-shadow: inset 0 0 0 1px var(--brand-violet);
}
.drv__kpiNum {
  display: block; font-size: 24px; font-weight: 750; letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.drv__kpiLbl {
  display: block; margin-top: 2px; font-size: 11.5px; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-tertiary);
}

.drv__card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.drv__toolbar {
  display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.drv__field label {
  display: block; margin-bottom: 3px; font-size: 11px; font-weight: 600;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.drv__spacer { flex: 1; }

.drv__who { display: flex; align-items: center; gap: 10px; }
.drv__avatar {
  width: 32px; height: 32px; flex: none; border-radius: 50%;
  display: grid; place-items: center; background: var(--avatar-bg);
  color: #fff; font-size: 11px; font-weight: 700;
}
.drv__name { font-weight: 600; color: var(--text-primary); }
.drv__meta { font-size: 12px; color: var(--text-tertiary); }
.drv__num { font-variant-numeric: tabular-nums; }
.drv__dash { color: var(--text-tertiary); }

.drv__zones { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
.drv__zone {
  font-size: 11px; font-weight: 700; letter-spacing: 0.03em;
  color: var(--svc-fleet); background: var(--svc-fleet-bg);
  border-radius: 5px; padding: 1px 7px;
}
.drv__zoneMore { font-size: 11px; color: var(--text-tertiary); font-weight: 600; }
.drv__region {
  flex-basis: 100%; font-size: 11.5px; color: var(--text-tertiary); margin-top: 2px;
}
/* zona sul modello ad aree: si deve notare */
.drv__legacy { font-size: 12px; color: var(--state-attn); }

.drv__role {
  font-size: 11px; font-weight: 700; letter-spacing: 0.03em;
  text-transform: uppercase; color: var(--text-secondary);
  background: var(--bg-tertiary); border: 1px solid var(--border-color);
  border-radius: 5px; padding: 1px 7px;
}
.drv__role.is-jolly {
  color: var(--svc-fleet); background: var(--svc-fleet-bg); border-color: transparent;
}

.drv__foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; padding: 12px 14px;
  border-top: 1px solid var(--border-color);
}
.drv__footInfo { font-size: 12.5px; color: var(--text-tertiary); }

.drv :deep(.el-table__row) { cursor: pointer; }
/* header chiaro: l'override globale impone un gradiente viola con !important */
.drv :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 11px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
